import React, { useState } from "react"
import useTitle from "@/hooks/useTitle"
import { addIcon } from "@/assets/img/imgAssets"
import {
  PlusIcon,
  MinusIcon,
  TrashIcon,
  PencilSquareIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline"
import PulseLoader from "react-spinners/PulseLoader"

import {
  useGetOrgIntentionBoardsQuery,
} from "@/features/orgIntentionBoards/orgIntentionBoardsApiSlice"

import {
  useGetOrganizationsQuery,
} from "@/pages/admin/organizations/organizationsApiSlice"

const EtOrgIntentionBoardList = ({
  onAdd,
  onUpdate,
  onDelete,
}) => {

  // First Created - 04 Sep 2026
  // Author - Atul Pandey
  // Purpose - Admin list page for Trinity Intention Boards.
  // Updated - 04 Sep 2026 - Added organization filter and organization names.

  useTitle("Intention Boards")

  /*
  |--------------------------------------------------------------------------
  | 1 - State
  |--------------------------------------------------------------------------
  */

  const [
    showDetails,
    setShowDetails,
  ] =
    useState({})

  const [
    selectedOrgId,
    setSelectedOrgId,
  ] =
    useState("")

  /*
  |--------------------------------------------------------------------------
  | 2 - Get Organizations
  |--------------------------------------------------------------------------
  */

  const {
    data:
      organizationsData,

    isLoading:
      isLoadingOrganizations,

    isError:
      isOrganizationsError,

    error:
      organizationsError,

  } =
    useGetOrganizationsQuery()

  /*
  |--------------------------------------------------------------------------
  | 3 - Normalize Organizations
  |--------------------------------------------------------------------------
  */

  const organizations =
    organizationsData
      ?.ids
      ?.map(
        id =>
          organizationsData
            ?.entities[id]
      ) ||
    []

  /*
  |--------------------------------------------------------------------------
  | 4 - Get Intention Boards
  |--------------------------------------------------------------------------
  */

  const {
    data:
      orgIntentionBoardsData,

    isError,
    error,
    isLoading,

  } =
    useGetOrgIntentionBoardsQuery(

      selectedOrgId
        ? {
            orgId:
              selectedOrgId,
          }
        : undefined

    )

  /*
  |--------------------------------------------------------------------------
  | 5 - Toggle Details
  |--------------------------------------------------------------------------
  */

  const handleToggleDetails =
    intentionBoardId => {

      setShowDetails(
        prev => ({
          ...prev,

          [intentionBoardId]:
            !prev[
              intentionBoardId
            ],
        })
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 6 - Copy Public URL
  |--------------------------------------------------------------------------
  */

  const handleCopyPublicUrl =
    async publicSlug => {

      const publicUrl =
        `${window.location.origin}/trinity/intention/${publicSlug}`

      await navigator
        .clipboard
        .writeText(
          publicUrl
        )

    }

  /*
  |--------------------------------------------------------------------------
  | 7 - Format Deadline
  |--------------------------------------------------------------------------
  */

  const formatDeadline =
    dateValue => {

      if (
        !dateValue
      ) {
        return "Not Set"
      }

      return new Date(
        dateValue
      )
        .toLocaleString()

    }

  /*
  |--------------------------------------------------------------------------
  | 8 - Get Organization Name
  |--------------------------------------------------------------------------
  */

  const getOrganizationName =
    orgId => {

      const normalizedOrgId =
        orgId?._id ||
        orgId

      const organization =
        organizations
          ?.find(
            item =>
              item?._id ===
              normalizedOrgId
          )

      return (
        organization
          ?.organizationName ||
        "Unknown Organization"
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 9 - Loading
  |--------------------------------------------------------------------------
  */

  if (
    isLoading ||
    isLoadingOrganizations
  ) {

    return (
      <PulseLoader
        color={"#ea580c"}
      />
    )

  }

  /*
  |--------------------------------------------------------------------------
  | 10 - Error
  |--------------------------------------------------------------------------
  */

  if (
    isError
  ) {

    return (

      <div className="text-sm text-orange-700">
        Error occurred: {error?.data?.message}
      </div>

    )

  }

  if (
    isOrganizationsError
  ) {

    return (

      <div className="text-sm text-orange-700">

        Error occurred: {
          organizationsError
            ?.data
            ?.message ||
          "Unable to load organizations"
        }

      </div>

    )

  }

  /*
  |--------------------------------------------------------------------------
  | 11 - Normalize Intention Boards
  |--------------------------------------------------------------------------
  */

  const orgIntentionBoards =
    orgIntentionBoardsData
      ?.ids
      ?.map(
        id => {

          const board =
            orgIntentionBoardsData
              ?.entities[id]

          return {

            _id:
              board?._id,

            orgId:
              board?.orgId,

            trailId:
              board?.trailId,

            boardName:
              board?.boardName,

            publicSlug:
              board?.publicSlug,

            orgCode:
              board?.orgCode,

            headline:
              board?.headline,

            responseDeadline:
              board?.responseDeadline,

            timezone:
              board?.timezone,

            boardStatus:
              board?.boardStatus,

            isActive:
              board?.isActive,

            allowLateResponses:
              board?.allowLateResponses,

            createdAt:
              board?.createdAt,

          }

        }
      ) ||
    []

  /*
  |--------------------------------------------------------------------------
  | 12 - Render
  |--------------------------------------------------------------------------
  */

  return (

    <div className="bg-gray-200 m-1 p-1 rounded font-poppins mx-auto">

      {/* 1. Header */}

      <div className="flex justify-between items-center m-1 p-1">

        <div className="flex justify-start items-center">

          <p className="text-2xl m-1 p-1 font-bold">
            Intention Boards
          </p>

          <img
            src={addIcon}
            alt=""
            className="h-10 w-10 cursor-pointer"
            onClick={onAdd}
          />

        </div>

      </div>

      {/* 2. Organization Filter */}

      <div className="bg-white rounded m-1 p-2">

        <div className="flex justify-start items-center">

          <p className="text-xs font-bold text-gray-700 mr-2">
            Organization:
          </p>

          <select
            value={
              selectedOrgId
            }
            onChange={
              e =>
                setSelectedOrgId(
                  e.target.value
                )
            }
            className="rounded border border-gray-300 p-2 text-xs min-w-[240px]"
          >

            <option value="">
              All Organizations
            </option>

            {organizations
              ?.filter(
                organization =>
                  organization
                    ?.active !==
                  false
              )
              ?.sort(
                (
                  a,
                  b
                ) =>
                  (
                    a
                      ?.organizationName ||
                    ""
                  )
                    .localeCompare(
                      b
                        ?.organizationName ||
                      ""
                    )
              )
              ?.map(
                organization => (

                  <option
                    key={
                      organization
                        ?._id
                    }
                    value={
                      organization
                        ?._id
                    }
                  >

                    {
                      organization
                        ?.organizationName
                    }

                  </option>

                )
              )}

          </select>

        </div>

      </div>

      {/* 3. Empty State */}

      {orgIntentionBoards?.length === 0 && (

        <div className="bg-white rounded m-1 p-4">

          <p className="text-sm text-gray-600">

            {
              selectedOrgId
                ? "No Intention Boards found for the selected organization."
                : "No Intention Boards found. Click the add button to create one."
            }

          </p>

        </div>

      )}

      {/* 4. Board List */}

      {orgIntentionBoards?.map(
        (
          item,
          index
        ) => (

          <div
            key={item._id}
            className="m-1 p-1 bg-white rounded"
          >

            <div className="m-1 p-1 bg-gray-100 rounded">

              <div className="flex justify-between items-center">

                <div className="flex justify-start items-center mx-2">

                  <div
                    onClick={
                      () =>
                        handleToggleDetails(
                          item._id
                        )
                    }
                    className="m-1 p-1 cursor-pointer rounded-full h-7 w-7 flex justify-center items-center hover:bg-white"
                  >

                    {showDetails[item._id] ? (

                      <MinusIcon className="h-6 w-6 text-orange-600" />

                    ) : (

                      <PlusIcon className="h-4 w-4 text-orange-600" />

                    )}

                  </div>

                  <div className="m-1 p-1">

                    <div className="flex justify-start items-center flex-wrap">

                      <p className="font-bold">

                        {index + 1} - {item.boardName}

                      </p>

                      <p className="text-[10px] bg-orange-100 text-orange-700 rounded-full mx-2 px-2 py-1">

                        {item.boardStatus || "draft"}

                      </p>

                      <p
                        className={`text-[10px] rounded-full mx-1 px-2 py-1 ${
                          item.isActive
                            ? "bg-lime-100 text-lime-700"
                            : "bg-gray-200 text-gray-600"
                        }`}
                      >

                        {item.isActive ? "Active" : "Inactive"}

                      </p>

                    </div>

                    {/* Organization */}

                    <p className="text-[10px] text-orange-700 font-semibold mt-1">

                      {
                        getOrganizationName(
                          item.orgId
                        )
                      }

                    </p>

                    <p className="text-xs text-gray-600">

                      {
                        item.headline ||
                        "No headline added"
                      }

                    </p>

                    <p className="text-[10px] text-gray-500 mt-1">

                      Public Slug: {
                        item.publicSlug
                      }

                    </p>

                  </div>

                </div>

                {/* Actions */}

                <div className="flex justify-start items-center mx-2">

                  <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">

                    <ClipboardDocumentIcon
                      onClick={
                        () =>
                          handleCopyPublicUrl(
                            item.publicSlug
                          )
                      }
                      className="h-4 w-4 text-orange-600 cursor-pointer"
                    />

                  </div>

                  <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">

                    <PencilSquareIcon
                      onClick={
                        () =>
                          onUpdate(
                            item?._id
                          )
                      }
                      className="h-4 w-4 text-orange-600 cursor-pointer"
                    />

                  </div>

                  <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">

                    <TrashIcon
                      onClick={
                        () =>
                          onDelete(
                            item?._id
                          )
                      }
                      className="h-4 w-4 text-orange-600 cursor-pointer"
                    />

                  </div>

                </div>

              </div>

              {/* 5. Expanded Details */}

              {showDetails[item._id] && (

                <div className="m-1 p-1 bg-gray-200 rounded">

                  <div className="m-1 p-1 rounded bg-white">

                    {/* Organization */}

                    <div className="m-1 p-1">

                      <p className="text-xs font-bold">
                        Organization
                      </p>

                      <p className="text-[10px] text-gray-600">

                        {
                          getOrganizationName(
                            item.orgId
                          )
                        }

                      </p>

                    </div>

                    {/* Public URL */}

                    <div className="m-1 p-1">

                      <p className="text-xs font-bold">
                        Public URL
                      </p>

                      <p className="text-[10px] text-orange-700 break-all">

                        {window.location.origin}/trinity/intention/{item.publicSlug}

                      </p>

                    </div>

                    {/* Organization Code */}

                    <div className="m-1 p-1">

                      <p className="text-xs font-bold">
                        Organization Code
                      </p>

                      <p className="text-[10px] text-gray-600">

                        {
                          item.orgCode ||
                          "Not Set"
                        }

                      </p>

                    </div>

                    {/* Response Deadline */}

                    <div className="m-1 p-1">

                      <p className="text-xs font-bold">
                        Response Deadline
                      </p>

                      <p className="text-[10px] text-gray-600">

                        {
                          formatDeadline(
                            item.responseDeadline
                          )
                        }

                      </p>

                      <p className="text-[9px] text-gray-500">

                        {
                          item.timezone ||
                          "America/Los_Angeles"
                        }

                      </p>

                    </div>

                    {/* Board Status */}

                    <div className="m-1 p-1">

                      <p className="text-xs font-bold">
                        Board Status
                      </p>

                      <p className="text-[10px] text-gray-600">

                        {
                          item.boardStatus ||
                          "draft"
                        }

                      </p>

                    </div>

                    {/* Late Responses */}

                    <div className="m-1 p-1">

                      <p className="text-xs font-bold">
                        Late Responses
                      </p>

                      <p className="text-[10px] text-gray-600">

                        {
                          item.allowLateResponses
                            ? "Allowed"
                            : "Not Allowed"
                        }

                      </p>

                    </div>

                    {/* Guidance */}

                    <div className="m-1 p-1">

                      <p className="text-[10px] text-gray-500">

                        Use the pencil icon to update this Intention Board
                        and manage its questions and candidates.

                      </p>

                    </div>

                  </div>

                </div>

              )}

            </div>

          </div>

        )
      )}

    </div>

  )

}

export default EtOrgIntentionBoardList